import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { ServiceStatus, MachineSelector, TimeRangeSelector } from "../ui";
import { useAppContext, useAuth } from "../../context";
import { api } from "../../api";
import { cn } from "../../lib/utils";
import {
  Menu,
  X,
  LayoutDashboard,
  Package,
  Star,
  Lightbulb,
  Trophy,
  Settings,
} from "lucide-react";

interface MachineInfo {
  name: string;
  code: string;
  priceUsd: number;
}

const navItems = [
  { to: "/", label: "Overview", icon: LayoutDashboard },
  { to: "/inventory", label: "Inventory", icon: Package },
  { to: "/wins", label: "Wins", icon: Star },
  { to: "/advisor", label: "Advisor", icon: Lightbulb },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
];

export function Header() {
  const { selectedMachine, setSelectedMachine, timeRange, setTimeRange } = useAppContext();
  const { isAdmin } = useAuth();
  const [machineInfo, setMachineInfo] = useState<MachineInfo | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fetch machine info when selected machine changes
  useEffect(() => {
    async function fetchMachineInfo() {
      if (!selectedMachine) return;

      try {
        const response = await api.stats.machines({ identifier: selectedMachine }).get();
        if (response.data && "machine" in response.data) {
          const machine = response.data.machine as MachineInfo;
          setMachineInfo(machine);
        }
      } catch (error) {
        console.error("Failed to fetch machine info:", error);
      }
    }

    fetchMachineInfo();
  }, [selectedMachine]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [selectedMachine]);

  return (
    <header className="border-b border-border bg-card">
      <div className="container py-4">
        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-between">
          {/* Navigation */}
          <nav className="flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
            {isAdmin && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ml-2 border-l border-border pl-4",
                    isActive
                      ? "bg-amber-500/20 text-amber-400"
                      : "text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10"
                  )
                }
              >
                <Settings className="h-4 w-4" />
                Admin
              </NavLink>
            )}
          </nav>

          {/* Controls */}
          <div className="flex items-center gap-3">
            <ServiceStatus />
            <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            <MachineSelector value={selectedMachine} onChange={setSelectedMachine} />
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden space-y-3">
          {/* Top row with hamburger and status */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-secondary transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>

            <ServiceStatus />
          </div>

          {/* Second row with selectors */}
          <div className="flex items-center gap-2">
            <TimeRangeSelector value={timeRange} onChange={setTimeRange} className="flex-1" />
            <MachineSelector value={selectedMachine} onChange={setSelectedMachine} className="flex-1" />
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <nav className="mt-4 pt-4 border-t border-border space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    )
                  }
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </NavLink>
              ))}
              {isAdmin && (
                <NavLink
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors mt-2 border-t border-border pt-4",
                      isActive
                        ? "bg-amber-500/20 text-amber-400"
                        : "text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10"
                    )
                  }
                >
                  <Settings className="h-5 w-5" />
                  Admin
                </NavLink>
              )}
            </nav>
          )}
        </div>
      </div>

      {/* Machine Info Banner */}
      <div className="container pb-6 pt-2">
        <h1 className="text-xl md:text-2xl font-bold text-foreground">
          {machineInfo?.name ?? "Gap Tracker"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {machineInfo
            ? `Live stats from the CollectorCrypt ${machineInfo.name} (${machineInfo.code})`
            : "Select a machine to view stats"}
        </p>
      </div>
    </header>
  );
}
